import { datadogRum } from '@datadog/browser-rum';
import { FC, useCallback, useEffect, useRef, useState } from 'react';

import {
	Box,
	Button,
	ClipboardCopyOutline,
	HStack,
	Input,
	InputGroup,
	InputRightElement,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spinner,
	Text,
	useDisclosure,
	useToast,
	VideoCameraOutline,
	VStack,
} from '@bonusly/design-system';

interface BugRecorderProps {
	/** Variant for different visual contexts */
	variant?: 'banner' | 'floating';
}

type ModalState = 'confirm' | 'starting' | 'share' | null;

/**
 * BugRecorder - Record a bug session and share the Datadog replay URL
 *
 * Flow:
 * 1. Click "Record Bug" → Confirmation modal
 * 2. Click "Record" → Starts recording, button becomes "Stop Recording"
 * 3. Click "Stop Recording" → Shows URL modal
 * 4. Close → Reverts to "Record Bug", ready for new session
 */
export const BugRecorder: FC<BugRecorderProps> = ({ variant = 'banner' }) => {
	const [isRecording, setIsRecording] = useState(false);
	const [replayUrl, setReplayUrl] = useState<string | null>(null);
	const [modalState, setModalState] = useState<ModalState>(null);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const toast = useToast();
	const startTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	// Cleanup timeout on unmount to prevent memory leaks
	useEffect(() => {
		return () => {
			if (startTimeoutRef.current) {
				clearTimeout(startTimeoutRef.current);
			}
		};
	}, []);

	const handleRecordClick = useCallback(() => {
		if (isRecording) {
			// Stop recording and show share modal (URL was captured at start time)
			datadogRum.stopSessionReplayRecording();
			setIsRecording(false);
			setModalState('share');
			onOpen();
		} else {
			// Show confirmation modal
			setModalState('confirm');
			onOpen();
		}
	}, [isRecording, onOpen]);

	const handleStartRecording = useCallback(() => {
		// Check at click time, not render time
		const hasContext = Boolean(datadogRum.getInternalContext());

		if (hasContext) {
			// Force recording even if session wasn't sampled for replay
			// See: https://datadoghq.dev/browser-sdk/interfaces/_datadog_browser-rum.DatadogRum.html#startSessionReplayRecording
			datadogRum.startSessionReplayRecording({ force: true });

			// Capture the replay link immediately to get the correct start timestamp
			const startUrl = datadogRum.getSessionReplayLink();
			setReplayUrl(startUrl ?? null);

			// Show spinner while recording initializes
			setModalState('starting');

			// Clear any existing timeout
			if (startTimeoutRef.current) {
				clearTimeout(startTimeoutRef.current);
			}

			// Give Datadog time to start capturing before closing modal
			startTimeoutRef.current = setTimeout(() => {
				setIsRecording(true);
				setModalState(null);
				onClose();

				toast({
					title: 'Recording started',
					description: 'Reproduce the bug, then click "Stop Recording" to get the URL.',
					status: 'info',
					duration: 4000,
					isClosable: true,
				});
			}, 1500);
		} else {
			toast({
				title: 'Datadog RUM not available',
				description: 'Session replay requires Datadog RUM to be initialized.',
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	}, [onClose, toast]);

	const handleCopyUrl = useCallback(async () => {
		if (!replayUrl) return;

		try {
			await navigator.clipboard.writeText(replayUrl);
			toast({
				title: 'Copied!',
				description: 'Session replay URL copied to clipboard.',
				status: 'success',
				duration: 2000,
				isClosable: true,
			});
		} catch {
			toast({
				title: 'Failed to copy',
				description: 'Please select and copy the URL manually.',
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		}
	}, [replayUrl, toast]);

	const handleCloseShareModal = useCallback(() => {
		// Reset state for new recording
		setReplayUrl(null);
		setModalState(null);
		onClose();
	}, [onClose]);

	const handleCloseConfirmModal = useCallback(() => {
		setModalState(null);
		onClose();
	}, [onClose]);

	const bannerStyles = {
		bgColor: isRecording ? 'red.100' : 'grayAlpha.100',
		color: isRecording ? 'red.700' : 'gray.800',
		size: 'sm' as const,
		fontSize: 'md',
		px: 4,
		py: 1,
		borderRadius: '8px',
		_hover: { bgColor: isRecording ? 'red.100' : 'grayAlpha.100', cursor: 'pointer' },
		_active: { bgColor: isRecording ? 'red.100' : 'grayAlpha.100' },
	};

	const floatingStyles = {
		colorScheme: isRecording ? ('danger' as const) : ('primary' as const),
		size: 'md' as const,
	};

	const buttonStyles = variant === 'banner' ? bannerStyles : floatingStyles;

	return (
		<>
			<Button
				leftIcon={isRecording ? <Box w={2} h={2} borderRadius="full" bg="red.500" animation="pulse 1.5s infinite" /> : <VideoCameraOutline boxSize={4} />}
				onClick={handleRecordClick}
				{...buttonStyles}
				sx={{
					'@keyframes pulse': {
						'0%, 100%': { opacity: 1 },
						'50%': { opacity: 0.4 },
					},
				}}
			>
				{isRecording ? 'Stop Recording' : 'Record Bug'}
			</Button>

			{/* Confirmation Modal */}
			<Modal isOpen={isOpen && modalState === 'confirm'} onClose={handleCloseConfirmModal} size="md" isCentered>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Start Bug Recording</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Text color="text.soft">
							This will start recording your screen session. Reproduce the bug, then click &quot;Stop Recording&quot; to get a shareable URL for the development
							team.
						</Text>
					</ModalBody>
					<ModalFooter>
						<HStack spacing={3}>
							<Button variant="ghost" onClick={handleCloseConfirmModal}>
								Cancel
							</Button>
							<Button colorScheme="primary" onClick={handleStartRecording}>
								Record
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>

			{/* Starting Recording Modal */}
			<Modal isOpen={isOpen && modalState === 'starting'} onClose={() => {}} size="sm" isCentered closeOnOverlayClick={false}>
				<ModalOverlay />
				<ModalContent>
					<ModalBody py={8}>
						<VStack spacing={4}>
							<Spinner size="lg" color="primary.500" />
							<Text color="text.soft" textAlign="center">
								Starting recording...
							</Text>
						</VStack>
					</ModalBody>
				</ModalContent>
			</Modal>

			{/* Share URL Modal */}
			<Modal isOpen={isOpen && modalState === 'share'} onClose={handleCloseShareModal} size="lg" isCentered>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Share Bug Recording</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						{replayUrl ? (
							<>
								<Text mb={4} color="text.soft">
									Copy this URL and share it with the development team. They&apos;ll be able to see exactly what happened on your screen.
								</Text>
								<InputGroup size="md">
									<Input value={replayUrl} isReadOnly pr="4rem" fontFamily="mono" fontSize="sm" />
									<InputRightElement width="4rem">
										<Button h="1.75rem" size="sm" onClick={handleCopyUrl} variant="ghost">
											<ClipboardCopyOutline boxSize={4} />
										</Button>
									</InputRightElement>
								</InputGroup>
								<Text mt={3} color="text.softer" fontSize="xs">
									Note: The replay may show &quot;session in progress&quot; until you close or refresh this tab.
								</Text>
							</>
						) : (
							<Text color="text.softer" fontStyle="italic">
								No replay URL available. Session replay may not be enabled for this session.
							</Text>
						)}
					</ModalBody>
					<ModalFooter>
						<HStack spacing={3}>
							{replayUrl && (
								<Button colorScheme="primary" onClick={handleCopyUrl}>
									Copy URL
								</Button>
							)}
							<Button variant="ghost" onClick={handleCloseShareModal}>
								Done
							</Button>
						</HStack>
					</ModalFooter>
				</ModalContent>
			</Modal>
		</>
	);
};
